import { isPeerConnected } from './gunClient';
import { isLocalAck } from './gunAck';

type GunNode = any;
type Ack = any;

/**
 * Thrown by {@link prepareGunWrite} for a value Gun cannot store.
 *
 * Gun rejects arrays outright ("Invalid data: Array"), and it does so
 * asynchronously inside its own write path — so the failure used to surface as
 * data that simply never appeared, long after the call that caused it. Failing
 * at the call site instead makes the responsible write obvious. Serialize
 * arrays yourself (`JSON.stringify`) or model them as a Gun node of keyed
 * children.
 */
export class GunWriteShapeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GunWriteShapeError';
  }
}

/**
 * Strip GunDB internal fields (_ , # , >) from an object before writing.
 * Also removes undefined values deeply.
 *
 * Throws {@link GunWriteShapeError} for arrays at any depth: this function is
 * the last thing to inspect a value before it reaches Gun, so it is the right
 * place to reject a shape Gun will silently drop.
 */
export function prepareGunWrite(obj: any, path = ''): any {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    throw new GunWriteShapeError(
      `Gun cannot store an array (at ${path || 'the write root'}). ` +
        'JSON.stringify it, or write it as a node of keyed children.',
    );
  }

  const cleaned: any = {};
  for (const key of Object.keys(obj)) {
    // Skip GunDB internal fields
    if (key === '_' || key === '#' || key === '>') continue;
    const value = obj[key];
    if (value === undefined) continue;
    cleaned[key] = prepareGunWrite(value, path ? `${path}.${key}` : key);
  }
  return cleaned;
}

/** Options for {@link gunPut}. */
export type GunPutOptions = {
  /** Names the write in log output. Worth setting; the soul alone is cryptic. */
  label?: string;
  /** Receives every ack, error or not, after the shape guard has passed. */
  onAck?: (ack: Ack) => void;
};

/**
 * Fire-and-forget write — the shape most of this app does.
 *
 * `node.put(x)` is not a safe default, and the codebase had ~140 of them
 * against this module's ~5 guarded writes. Two things go wrong with a bare put,
 * both silently:
 *
 * - An unstorable value (an array at any depth) is rejected asynchronously
 *   inside Gun's own write path, so it surfaces as data that never appeared,
 *   arbitrarily far from the call that caused it.
 * - Nothing looks at the ack. A write the relay rejects — unsigned, oversized,
 *   unverified — is indistinguishable from one that worked.
 *
 * This gives both a home without forcing every caller to become async: the
 * value is cleaned and shape-checked up front, and a rejected write is logged
 * against its label rather than vanishing.
 *
 * Use {@link gunPutWithTimeout} / {@link gunPutWithRetry} instead when the
 * caller genuinely needs to await the outcome and can act on failure.
 */
export function gunPut(node: GunNode, data: any, opts?: GunPutOptions): void {
  const what = opts?.label ?? 'write';
  if (!node) {
    console.error(`[gun] ${what}: no node to write to`);
    return;
  }

  let cleaned: any;
  try {
    cleaned = prepareGunWrite(data);
  } catch (e) {
    // Not thrown on: these calls sit in loops and event handlers where an
    // exception would abandon the rest of the pass. Loud is enough — before
    // this, Gun dropped the write with no message tied to a call site at all.
    console.error(`[gun] ${what}: refusing to write an unstorable value:`, e);
    return;
  }

  try {
    node.put(cleaned, (ack: Ack) => {
      if (ack?.err) console.error(`[gun] ${what}: write rejected:`, ack.err);
      opts?.onAck?.(ack);
    });
  } catch (e) {
    console.error(`[gun] ${what}: put threw:`, e);
  }
}

/**
 * Outcome of an awaited write.
 *
 * A RESULT, never a rejection. Every failure this layer can name is a normal
 * outcome of talking to an eventually-consistent graph over a flaky socket —
 * the relay refused the write, nothing acked in time — and modelling those as
 * exceptions meant each caller chose for itself whether to try/catch. The
 * layer's other write paths (`signedRecord.write`, `sealedCollection.write`)
 * already report failure as a value; these now agree with them, so there is one
 * convention to learn rather than one per function.
 *
 * `confirmedByPeer` distinguishes "durable on this device" from "a relay has
 * it". Both are `ok`: the data IS stored locally and Gun replays it when a peer
 * returns, so an offline write is a success with a caveat, not a failure.
 */
export type PutResult =
  | { ok: true; ack: Ack; confirmedByPeer: boolean }
  | { ok: false; error: string; ack?: Ack };

/**
 * Put data to a Gun node, resolving when the write is acknowledged.
 *
 * Gun's put callback fires for BOTH the local store's ack and a relay's, with
 * the same `{ok: 1}` shape. Resolving on whichever came first meant a write the
 * relay rejects still resolved successfully — the local IndexedDB adapter had
 * already acked it, and the relay's `err` arrived after the promise settled.
 *
 * So a local-only ack no longer ends the wait while a peer is connected: we
 * keep listening for the peer's verdict and fail if it is an error. Offline,
 * the local store is the only authority there is, so its ack resolves at once.
 * If the timeout expires with only a local ack in hand, that is reported as a
 * success with `confirmedByPeer: false` rather than a failure — the data IS
 * durable on this device and Gun will replay it when a peer returns.
 *
 * Never rejects; see {@link PutResult}. A value Gun cannot store is the one
 * exception, because that is a bug in the CALLER rather than a fact about the
 * network — see {@link GunWriteShapeError}.
 */
export function gunPutWithTimeout(
  node: GunNode,
  data: any,
  timeoutMs = 1000
): Promise<PutResult> {
  // Outside the promise: a caller passing an unstorable value has made a
  // programming error, and surfacing it as a rejected promise buried it in the
  // same `if (!res.ok)` branch as a flaky relay.
  const cleaned = prepareGunWrite(data);

  return new Promise((resolve) => {
    if (!node) {
      resolve({ ok: false, error: 'no node to write to' });
      return;
    }

    let settled = false;
    let localAck: Ack = null;

    const settle = (value: PutResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };

    const timer = setTimeout(() => {
      if (localAck) {
        // Durable here, unconfirmed there.
        settle({ ok: true, ack: localAck, confirmedByPeer: false });
        return;
      }
      settle({ ok: false, error: `Gun put timeout after ${timeoutMs}ms` });
    }, timeoutMs);

    try {
      node.put(cleaned, (ack: Ack) => {
        if (settled) return;
        // Gun does not guarantee an ack object; reading `.err` off undefined
        // threw inside Gun's own callback, where nothing catches it.
        if (ack?.err) {
          settle({ ok: false, error: String(ack.err), ack });
          return;
        }
        if (isLocalAck(ack)) {
          localAck = ack;
          // With no peer there is nothing further to wait for.
          if (!isPeerConnected()) settle({ ok: true, ack, confirmedByPeer: false });
          return;
        }
        settle({ ok: true, ack, confirmedByPeer: true });
      });
    } catch (e) {
      settle({ ok: false, error: e instanceof Error ? e.message : String(e) });
    }
  });
}

/**
 * Helper to perform a Gun put with automatic retry on failure.
 * Retries up to 3 times with exponential backoff.
 *
 * A write that only the local store acknowledged counts as success and is NOT
 * retried: the data is durable on this device and Gun replays it to peers on
 * its own, so retrying would only duplicate the write. The result reports
 * `confirmedByPeer` for callers that need to know the difference.
 *
 * A shape error ({@link GunWriteShapeError}) is not retried either — the write
 * is invalid and will fail identically every time, and it throws rather than
 * returning, for the reason given on {@link gunPutWithTimeout}.
 */
export async function gunPutWithRetry(
  node: GunNode,
  data: any,
  maxRetries = 3,
  baseDelay = 500
): Promise<PutResult> {
  let last: PutResult = { ok: false, error: 'no attempt was made' };

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    last = await gunPutWithTimeout(node, data, 1000);
    if (last.ok) return last;
    if (attempt < maxRetries) {
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return last;
}

// NOTE: getUserNode() was removed. It resolved a user's records in the
// world-writable public namespace (`timeTracker/<pub>/...`) and had no callers,
// so it was a trap waiting for the next author. Owned records live in the SEA
// user graph — use `ownedRoot(pub)` from gunClient.

/**
 * ------------------------------------------------------------------
 * Safe subscription helpers
 * ------------------------------------------------------------------
 * Gun's `chain.off()` takes NO handler argument and is destructive: it wipes
 * `cat.any` (every listener registered on that chain) and recursively offs
 * child/map chains. Because Gun memoizes chains per soul, two components
 * subscribing to the same node share one chain — so one component unmounting
 * and calling `node.off()` silently kills the other's live subscription.
 *
 * The only per-listener teardown Gun offers is the event object handed to the
 * callback (4th argument), whose `.off()` deletes just that listener's entry.
 * These helpers capture it and expose an unsubscribe that is safe to call even
 * before the first event arrives.
 */
export type GunUnsub = () => void;

/**
 * Grab the listener record Gun just registered on `chain`.
 *
 * Gun hands the per-listener event object to the CALLBACK (4th argument), which
 * is useless for a node that never emits: teardown then had nothing to detach
 * and the listener sat in `cat.any` for the rest of the session. Reading a
 * `.once()` on a soul the relay never answers for leaked one listener per call.
 *
 * But Gun registers that same object synchronously — `chain.on(fn)` adds it to
 * `chain._.any` under a fresh random key before returning (gun.js, `Gun.chain.get`
 * with a function argument). Diffing the keys around the call recovers it
 * immediately, so unsubscribe works whether or not the node ever fired.
 */
function captureNewListener(chain: GunNode, before: Set<string> | null): any {
  if (!before) return null;
  try {
    const any = chain?._?.any;
    if (!any) return null;
    for (const key of Object.keys(any)) {
      if (!before.has(key)) return any[key];
    }
  } catch {
    // Internals differ across Gun versions; fall back to the callback's event.
  }
  return null;
}

function listenerKeys(chain: GunNode): Set<string> | null {
  try {
    const any = chain?._?.any;
    return any ? new Set(Object.keys(any)) : new Set<string>();
  } catch {
    return null;
  }
}

function onWithEvent(
  chain: GunNode,
  handler: (data: any, key: string) => void,
): GunUnsub {
  let stopped = false;
  let event: any = null;

  const offEvent = () => {
    try {
      event?.off?.();
    } catch {
      // Ignore cleanup errors.
    }
  };

  try {
    const before = listenerKeys(chain);
    chain.on((data: any, key: string, _msg: any, eve: any) => {
      // Gun hands us the event on every emit; remember it so unsubscribe works
      // even if the synchronous capture below found nothing.
      if (eve && !event) event = eve;
      if (stopped) {
        offEvent();
        return;
      }
      handler(data, key);
    });
    event = captureNewListener(chain, before) ?? event;
  } catch {
    return () => {};
  }

  return () => {
    if (stopped) return;
    stopped = true;
    offEvent();
  };
}

/**
 * Subscribe to a single Gun node. Returns a per-listener unsubscribe that does
 * NOT tear down other listeners on the same (memoized) chain.
 */
export function gunOn(
  node: GunNode,
  handler: (data: any, key: string) => void,
): GunUnsub {
  if (!node) return () => {};
  return onWithEvent(node, handler);
}

/**
 * How long to keep listening after `.once()` has reported nothing, before
 * concluding a record really is absent. Covers the read-after-write window:
 * a node written moments ago is often not materialized when `.once()` samples
 * it, and `.once()` never looks again.
 */
const ABSENT_GRACE_MS = 1500;

/**
 * Is this a real value, or just Gun's metadata shell?
 *
 * `.on()` frequently fires for a node before its fields arrive, handing back an
 * object carrying only `_` (the soul and HAM state). Treating that as "the
 * answer" resolves a read with an empty record: the engagement read came back
 * as a shell, `fromStored` could not decode it, and a payment failed with
 * `recipients_unresolved` even though the page had the engagement on screen.
 */
function hasContent(data: unknown): boolean {
  if (data === null || data === undefined) return false;
  if (typeof data !== 'object') return true;
  return Object.keys(data as Record<string, unknown>).some((k) => k !== '_');
}

/**
 * Read a node once, resolving `null` if Gun does not produce a value in time.
 *
 * Two Gun behaviours make the obvious implementation wrong, and both cost real
 * debugging time here:
 *
 * 1. `.once()` fires only when there is data to report. For a soul the client
 *    has never seen, with a slow or absent peer, the callback never runs — so
 *    `new Promise((r) => node.once(r))` does not "return nothing", it hangs
 *    forever, and every `await` behind it hangs too.
 * 2. `.once()` samples whatever is materialized at that instant and never looks
 *    again. Immediately after a `.put()` the node frequently is not, so a
 *    read-after-write reports nothing — which surfaced as a funding invoice
 *    failing with `not_found` the moment it was created.
 *
 * So this listens with `.on()`, which fires both for what is already cached and
 * for what arrives later, and resolves on the first real value. A `.once()`
 * runs alongside purely to make genuine absence fast: when it reports nothing,
 * the listener is given a short grace period before `null` is concluded.
 *
 * Treat `null` as "unknown", not "absent" — the record may still be in flight.
 * Prefer `gunOn` wherever the caller can react to data arriving late. When the
 * difference between "definitely not there" and "no answer yet" matters — as it
 * does for any security decision that must fail closed — use
 * {@link gunOnceDetailed}, which reports which one happened.
 */
export function gunOnce<T = any>(
  node: GunNode,
  timeoutMs = 5000,
): Promise<T | null> {
  return gunOnceDetailed<T>(node, timeoutMs).then((r) => r.value);
}

/**
 * Outcome of a one-shot read.
 *
 * - `found`   — a real value arrived.
 * - `absent`  — Gun affirmatively reported nothing for this soul, and nothing
 *               turned up during the grace period. As close to "does not exist"
 *               as an eventually-consistent graph gets.
 * - `unknown` — no signal at all before the timeout. Says nothing about whether
 *               the record exists.
 *
 * The distinction is not academic: callers that need it were each hand-rolling
 * a `.once()` plus a timer to get it, and every one of them had to decide,
 * separately, what a silent node means. Deciding it wrong is a real
 * vulnerability — the friend-notice replay guard reads a high-water mark, and
 * treating "no answer" as "never seen" would let a captured notice replay
 * whenever the relay was slow.
 */
export type GunReadStatus = 'found' | 'absent' | 'unknown';
export type GunReadResult<T> = { value: T | null; status: GunReadStatus };

/** {@link gunOnce}, reporting absent-vs-unknown. See {@link GunReadResult}. */
export function gunOnceDetailed<T = any>(
  node: GunNode,
  timeoutMs = 5000,
): Promise<GunReadResult<T>> {
  if (!node) return Promise.resolve({ value: null, status: 'unknown' });
  return new Promise<GunReadResult<T>>((resolve) => {
    let settled = false;
    let off: GunUnsub = () => {};
    let graceTimer: ReturnType<typeof setTimeout> | null = null;

    const finish = (value: T | null, status: GunReadStatus) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (graceTimer) clearTimeout(graceTimer);
      try { off(); } catch { /* teardown is best effort */ }
      resolve({ value, status });
    };

    const timer = setTimeout(() => finish(null, 'unknown'), timeoutMs);

    try {
      off = gunOn(node, (data: any) => {
        if (hasContent(data)) finish(data as T, 'found');
      });
      // Gun delivers cached data SYNCHRONOUSLY inside .on() registration, so
      // the handler can settle before the assignment above completes — in which
      // case finish() called the initial no-op and the real listener would leak
      // for the rest of the session.
      if (settled) { try { off(); } catch { /* teardown is best effort */ } }
    } catch {
      /* fall through to the .once() below */
    }

    try {
      // The one legitimate `.once()` in the codebase: this IS the wrapper the
      // no-restricted-syntax rule points every other call site at.
      node.once((data: any) => {
        if (hasContent(data)) {
          finish(data as T, 'found');
          return;
        }
        // Gun answered, and the answer was "nothing". Give the live listener a
        // moment before calling it absent, rather than trusting a single sample.
        if (!graceTimer && !settled) {
          graceTimer = setTimeout(() => finish(null, 'absent'), ABSENT_GRACE_MS);
        }
      });
    } catch {
      finish(null, 'unknown');
    }
  });
}

/**
 * Read an index node and every record it points at, once.
 *
 * This shape — a node of ids, plus one node per record — is how a collection is
 * stored in Gun, and reading it by hand kept going wrong the same way: a
 * counter incremented per child and compared against the index length, so a
 * single child Gun never answered for left the whole read unresolved, and every
 * caller awaiting it hung permanently. Each child here is read independently on
 * its own timeout, so one missing record costs one entry rather than the list.
 *
 * Children that do not answer are omitted. The `_` metadata key is skipped.
 */
/**
 * How many child reads may be in flight at once.
 *
 * Each `gunOnce` holds a live `.on()` listener for the length of its timeout,
 * so reading an index of N children all at once put 2N listeners on the graph
 * and asked the relay for N souls in a single burst — the shape that makes a
 * relay drop messages, which then makes the reads time out, which looks like
 * missing data. Batching keeps the outstanding set bounded regardless of
 * collection size; total wall time is unchanged for small indexes.
 */
const ONCE_ALL_CONCURRENCY = 12;

/**
 * How long the index read keeps listening after each emit before concluding
 * the key set is complete. A relay streams a large index incrementally, so the
 * FIRST emit routinely carries only a few of its keys — resolving on it (what
 * `gunOnce` correctly does for a record body) silently truncated whole
 * collections to whatever arrived in the first packet.
 */
const INDEX_QUIET_MS = 500;

/**
 * Read an index node's key set, accumulating across emits until a quiet
 * period. Returns null when nothing arrived at all (unknown, not empty).
 */
function gunOnceIndexKeys(
  node: GunNode,
  timeoutMs: number,
): Promise<string[] | null> {
  return new Promise((resolve) => {
    const keys = new Set<string>();
    let sawAnything = false;
    let done = false;
    let quietTimer: ReturnType<typeof setTimeout> | undefined;
    let off: GunUnsub = () => {};

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(quietTimer);
      clearTimeout(hardTimer);
      try { off(); } catch { /* teardown is best effort */ }
      resolve(sawAnything ? Array.from(keys) : null);
    };

    const hardTimer = setTimeout(finish, timeoutMs);

    const absorb = (data: any) => {
      if (done || !data || typeof data !== 'object') return;
      for (const k of Object.keys(data)) {
        if (k !== '_') {
          keys.add(k);
          // Only a real key counts as an answer. A metadata-only shell (`_`
          // alone) arrives before the key set streams in; counting it made the
          // read return [] ("empty collection") for a node whose keys simply
          // had not arrived yet, instead of null ("unknown").
          sawAnything = true;
        }
      }
      clearTimeout(quietTimer);
      quietTimer = setTimeout(finish, INDEX_QUIET_MS);
    };

    try {
      off = gunOn(node, absorb);
      // Same synchronous-emit guard as gunOnceDetailed: if anything settled
      // during registration, the initial no-op `off` was the one called.
      if (done) { try { off(); } catch { /* teardown is best effort */ } }
    } catch {
      /* fall through to the .once() below */
    }

    try {
      // Allowed here for the same reason as in gunOnceDetailed: this file IS
      // the wrapper. The sample seeds the key set on nodes where the live
      // listener cannot attach.
      node.once(absorb);
    } catch {
      finish();
    }
  });
}

export async function gunOnceAll<T = any>(
  node: GunNode,
  timeoutMs = 5000,
): Promise<Record<string, T>> {
  const keys = (await gunOnceIndexKeys(node, timeoutMs)) ?? [];
  if (keys.length === 0) return {};

  const out: Record<string, T> = {};
  for (let i = 0; i < keys.length; i += ONCE_ALL_CONCURRENCY) {
    const slice = keys.slice(i, i + ONCE_ALL_CONCURRENCY);
    const entries = await Promise.all(
      slice.map(async (key) => [key, await gunOnce<T>(node.get(key), timeoutMs)] as const),
    );
    for (const [key, value] of entries) {
      if (value !== null && value !== undefined) out[key] = value;
    }
  }
  return out;
}

/**
 * Sweep every child of a node once, settling on a quiet period.
 *
 * Gun gives NO completion signal for a collection read: `map().once()` fires
 * per child as records arrive and never says "that was all". The only workable
 * termination is a quiet period re-armed by each arrival, so a slow relay takes
 * longer rather than silently truncating the result — plus a hard ceiling so an
 * endless stream cannot hang the caller.
 *
 * That logic was hand-rolled in three places with three different quiet
 * windows. Prefer {@link gunOnceAll} when the collection is an INDEX of ids
 * pointing at record nodes; use this when the children carry the data itself.
 */
export function gunScanOnce<T = any>(
  node: GunNode,
  opts?: { quietMs?: number; maxMs?: number; accept?: (data: any, key: string) => boolean },
): Promise<Map<string, T>> {
  const quietMs = opts?.quietMs ?? 600;
  const maxMs = opts?.maxMs ?? 10000;
  if (!node) return Promise.resolve(new Map<string, T>());

  return new Promise<Map<string, T>>((resolve) => {
    const out = new Map<string, T>();
    let done = false;
    let quietTimer: ReturnType<typeof setTimeout> | undefined;
    let off: GunUnsub = () => {};

    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(quietTimer);
      clearTimeout(hardTimer);
      try { off(); } catch { /* teardown is best effort */ }
      resolve(out);
    };

    const arm = () => {
      clearTimeout(quietTimer);
      quietTimer = setTimeout(finish, quietMs);
    };

    const hardTimer = setTimeout(finish, maxMs);
    arm();

    off = gunMapOn(node, (data: any, key: string) => {
      if (done || !data) return;
      if (opts?.accept && !opts.accept(data, key)) return;
      out.set(key, data as T);
      arm();
    });
    // Synchronous-emit guard — see gunOnceDetailed.
    if (done) { try { off(); } catch { /* teardown is best effort */ } }
  });
}

/**
 * Subscribe to every child of a Gun node (`node.map().on(...)`).
 * Returns a per-listener unsubscribe — see `gunOn`.
 *
 * Gun's metadata key `_` is filtered out HERE, once. It used to be every
 * caller's job, and the callers that forgot fed Gun's HAM state into a record
 * parser as though it were a row. An empty key is dropped for the same reason.
 */
export function gunMapOn(
  node: GunNode,
  handler: (data: any, key: string) => void,
): GunUnsub {
  if (!node) return () => {};
  let mapped: GunNode;
  try {
    mapped = node.map();
  } catch {
    return () => {};
  }
  return onWithEvent(mapped, (data: any, key: string) => {
    if (!key || key === '_') return;
    handler(data, key);
  });
}

/**
 * Fire `onChange` whenever any child of `node` is written, coalescing bursts.
 *
 * The point is refresh *triggering*, not data delivery: several screens read a
 * whole collection through a one-shot loader and used to re-run it on a 30s
 * timer, which is both stale (up to 30s behind) and wasteful (a full read every
 * 30s whether or not anything changed). Watching the index node turns those into
 * event-driven reloads.
 *
 * Gun replays existing children on subscribe, so the initial burst is expected;
 * `debounceMs` collapses it into a single call. Callers that already did an
 * initial load can ignore that first invocation or simply let it re-load once.
 */
export function onNodeChange(
  node: GunNode | null | undefined,
  onChange: () => void,
  debounceMs = 400,
): GunUnsub {
  if (!node) return () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const off = gunMapOn(node, (_data: any, key: string) => {
    if (stopped || !key || key === '_') return;
    // Fixed window from the first event, not a debounce. A relay streaming a
    // subgraph emits without pause, and resetting the deadline on every event
    // meant `onChange` was never called — the reload it triggers simply never
    // happened. The window still collapses the initial replay into one call.
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      if (!stopped) onChange();
    }, debounceMs);
  });

  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    off();
  };
}

/**
 * GunDB subscription options.
 */
export interface GunSubscriptionOptions<T> {
  onUpsert?: (data: T, key: string) => void;
  onDelete?: (key: string) => void;
  onError?: (error: Error) => void;
  batchMs?: number; // Batch updates, default 150ms
  transform?: (raw: any) => T | null; // Data transformation
}

/**
 * GunDB subscription handle.
 */
export interface GunSubscription {
  unsubscribe: () => void;
}

/**
 * Subscribe to a GunDB node with centralized pattern.
 * Handles .map().on() with batching, error handling, and cleanup.
 *
 * Mirrors the useGunSubscription hook logic for non-React code.
 *
 * @param node - Gun node to subscribe to
 * @param options - Callbacks and configuration
 * @returns Subscription handle with unsubscribe function
 *
 * @example
 * const sub = subscribeToGunNode(usersNode, {
 *   onUpsert: (user, key) => console.log('User:', user),
 *   onDelete: (key) => console.log('Deleted:', key),
 *   batchMs: 150
 * });
 * // later: sub.unsubscribe()
 */
export function subscribeToGunNode<T>(
  node: GunNode | null | undefined,
  options: GunSubscriptionOptions<T>
): GunSubscription | null {
  const { onUpsert, onDelete, onError, batchMs = 150, transform } = options;

  if (!node) {
    onError?.(new Error('Invalid Gun node'));
    return null;
  }

  // Batch accumulator
  const batch = new Map<string, { data: any; isDelete: boolean }>();
  let batchTimer: ReturnType<typeof setTimeout> | null = null;
  let offMap: GunUnsub = () => {};

  // Flush accumulated batch
  const flushBatch = () => {
    batchTimer = null;
    if (batch.size === 0) return;
    // A subscriber that throws must cost its own entry, not the batch: without
    // the per-entry catch one bad row aborted the forEach, skipped the clear
    // below, and (the timer already being null) the stale entries were
    // re-delivered on the next flush — duplicate upserts behind a permanently
    // wedged row.
    try {
      batch.forEach(({ data, isDelete }, key) => {
        try {
          if (isDelete) {
            onDelete?.(key);
          } else {
            const transformed = transform ? transform(data) : (data as T);
            // `!= null`, not `!== null`: a transform written as "return nothing
            // for an invalid row" returns undefined, which must be dropped the
            // same way.
            if (transformed != null) {
              onUpsert?.(transformed, key);
            }
          }
        } catch (err) {
          onError?.(err instanceof Error ? err : new Error(String(err)));
        }
      });
    } finally {
      batch.clear();
    }
  };

  // Schedule a flush `batchMs` after the FIRST queued event, and do not push
  // that deadline back when more arrive. Resetting the timer on every event
  // made this a debounce: Gun's `.map().on()` fires continuously while a relay
  // streams a subgraph, so the flush was starved and the data never reached
  // its subscriber at all. A fixed window still coalesces bursts, but always
  // delivers.
  const scheduleBatchFlush = () => {
    if (batchTimer) return;
    batchTimer = setTimeout(flushBatch, batchMs);
  };

  try {
    // Setup subscription (per-listener teardown, see gunMapOn)
    offMap = gunMapOn(node, (data: any, key: string) => {
      if (!key) return;

      if (data === null) {
        batch.set(key, { data: null, isDelete: true });
      } else if (data === undefined) {
        // Not a value and not a tombstone: Gun reports an unloaded child this
        // way. Handing it to `transform`/`onUpsert` fabricated a garbage row.
        return;
      } else {
        batch.set(key, { data, isDelete: false });
      }
      scheduleBatchFlush();
    });
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    onError?.(error);
    return null;
  }

  // Return unsubscribe function
  return {
    unsubscribe: () => {
      if (batchTimer) {
        clearTimeout(batchTimer);
        batchTimer = null;
      }
      offMap();
    },
  };
}
